import {
  FieldSchema,
  FormControlDependsOnSchema,
  FormControlDependsOnThenEnum,
  FormControlSchema,
  Schema,
  isObjectFieldSchema,
} from '@cak-legacy/generation-core';
import { IsRequiredValidation } from '@cak-legacy/generation-form';
import { FormSchemaModelMapper } from '@cak-legacy/generation-schema-core';
import {
  IPageFlowStep,
  OverviewEditLink,
  recursiveFlattenIncludingObjectName,
} from '@cak-legacy/shared-generic-rekenmodule';
import * as React from 'react';
import { summaryAndSendStep as generateOverviewSchema } from '../form-stepper/flow/flow-steps/summaryAndSendStep';
import { IFlowSteps } from '../interface/form';

/**
 * Recursivly converts the flowSteps to schemas as a linked list.
 * @param flowSteps The definition of the form as flow steps.
 * @param index The index of the current step to convert.
 * @param jsonSchemaMapper The mapper to convert the flow steps to schemas.
 * @returns The converted flow step at the given index.
 */
const getPageFlowStep = (
  flowSteps: IFlowSteps[],
  index: number,
  jsonSchemaMapper: FormSchemaModelMapper
): IPageFlowStep => {
  const stepName = flowSteps[index].name;
  const categoryName = flowSteps[index].categoryName;
  const flowStep = flowSteps[index];
  const getFormStepSchema = () => {
    let formStepSchema: Schema = {};
    const sections = flowStep.sections;
    if (sections) {
      sections.map((_section, sectionIndex) => {
        formStepSchema = {
          ...formStepSchema,
          ...jsonSchemaMapper.map(sections[sectionIndex]),
        };
      });
    }

    const addOverviewToProperties = (formStepSchema: any) => {
      for (const key in formStepSchema) {
        if (formStepSchema.hasOwnProperty(key)) {
          const sectionEl = formStepSchema[key];

          if (sectionEl.type === 'object') {
            addOverviewToProperties(sectionEl.properties);
            sectionEl.overview = {
              render: (_: string) => {
                return <OverviewEditLink value={''} flowName={categoryName} sectionName={key} />;
              },
            };
          }
        }
      }
    };

    addOverviewToProperties(formStepSchema);
    return formStepSchema;
  };
  const pageFlowStep: IPageFlowStep = {
    name: stepName,
    categoryName: categoryName,
    getSchema: (variables: any) => () => {
      const schema = getFormStepSchema();
      const flattenedVariables = {};
      recursiveFlattenIncludingObjectName(variables, flattenedVariables, '');
      handleDependsOnCrossSchema(flattenedVariables, schema, schema);
      return schema;
    },
    clearStepsAfterChange: [], // voorlopig niks doen.
    customClass: flowStep.isIntroduction ? 'form-step-introduction' : undefined,
  };

  if (flowSteps[index]?.introText) {
    pageFlowStep.introText = flowSteps[index].introText;
  }

  if (flowSteps[index]?.outroText) {
    pageFlowStep.outroText = flowSteps[index].outroText;
  }

  index++;
  pageFlowStep.nextFlowStep =
    index <= flowSteps.length - 1 ? getPageFlowStep(flowSteps, index, jsonSchemaMapper) : undefined;
  return pageFlowStep;
};

/**
 * Converts the flowsteps to a schema and returns the first flow step.
 * The schema is provided as a linked list with .nextFlowStep.
 * At the end a summary is generated based on the generated schemas of the previous steps.
 * @param {IFlowSteps[]} flowSteps - The flow step definition to convert.
 * @param {FormSchemaModelMapper} jsonSchemaMapper - The mapper to convert the flow steps to schemas.
 * @returns {IPageFlowStep} The first flow step.
 */
export const convertToSchema = (flowSteps: IFlowSteps[], jsonSchemaMapper: FormSchemaModelMapper): IPageFlowStep => {
  let firstStep = getPageFlowStep(flowSteps, 0, jsonSchemaMapper);
  let lastStep = firstStep;
  while (lastStep.nextFlowStep) {
    lastStep = lastStep.nextFlowStep;
  }
  lastStep.nextFlowStep = generateOverviewSchema(firstStep);
  return firstStep;
};

const handleDependsOnCrossSchema = (
  variables: any,
  schema: Record<string, FieldSchema>,
  recordsToCheck: Record<string, FieldSchema>
) => {
  for (const key in recordsToCheck) {
    if (recordsToCheck.hasOwnProperty(key)) {
      const fieldSchema = recordsToCheck[key];
      if (fieldSchema.dependsOn && thereExistsACrossSchemaDependsOn(fieldSchema, schema)) {
        const entriesToDelete: number[] = [];
        for (let i = 0; i < fieldSchema.dependsOn.length; i++) {
          const dependsOn = fieldSchema.dependsOn[i];
          if (dependsOnIsCrossSchema(dependsOn, schema)) {
            const ifResult = dependsOn.if(variables);
            if (ifResult) {
              handleThenDictionary[dependsOn.then](key, recordsToCheck);
            }

            entriesToDelete.push(i);
          }
        }
        fieldSchema.dependsOn = fieldSchema.dependsOn.filter((_, index) => !entriesToDelete.includes(index));

        if (fieldSchema.dependsOn.length === 0) {
          fieldSchema.dependsOn = undefined;
        }
      }

      if (isObjectFieldSchema(fieldSchema)) {
        handleDependsOnCrossSchema(variables, schema, fieldSchema.properties);
      }
    }
  }
};

const thereExistsACrossSchemaDependsOn = (fieldSchema: FieldSchema, schema: Record<string, FieldSchema>) => {
  if (!fieldSchema?.dependsOn || fieldSchema.dependsOn.length <= 0) {
    return false;
  }

  return fieldSchema.dependsOn.some((dependsOn) => dependsOnIsCrossSchema(dependsOn, schema));
};

const dependsOnIsCrossSchema = (dependsOn: FormControlDependsOnSchema, schema: Record<string, FieldSchema>) => {
  return (
    dependsOn.isAbsolutePropertyPath === true &&
    !(dependsOn.properties as string[]).some((propertyName) => fieldExistsOnSchema(schema, propertyName))
  );
};

const fieldExistsOnSchema = (schema: Record<string, FieldSchema>, fieldName: string): boolean => {
  const splittedFieldNames = fieldName.split('.');
  if (splittedFieldNames.length === 0) {
    return false;
  }

  let fields: Record<string, FieldSchema> = schema;

  if (splittedFieldNames.length >= 2) {
    // Loop through all the parent/object fields
    for (let i = 0; i < splittedFieldNames.length - 1; i++) {
      let parentField = fields[splittedFieldNames[i]];
      if (!parentField || !isObjectFieldSchema(parentField)) {
        return false;
      }

      fields = parentField.properties;
    }
  }

  return fields.hasOwnProperty(splittedFieldNames[splittedFieldNames.length - 1]);
};

const handleThenDictionary = {
  [FormControlDependsOnThenEnum.Hide]: (key: string, records: Record<string, FieldSchema>) => {
    delete records[key];
  },
  [FormControlDependsOnThenEnum.Disable]: (key: string, records: Record<string, FieldSchema>) => {
    let formControlSchema = records[key] as FormControlSchema;
    formControlSchema.disabled = true;
  },
  [FormControlDependsOnThenEnum.IsRequired]: (key: string, records: Record<string, FieldSchema>) => {
    let formControlSchema = records[key] as FormControlSchema;
    if (!formControlSchema) {
      return;
    }

    if (!formControlSchema.validate) {
      formControlSchema.validate = [];
    }

    if (typeof formControlSchema.validate === 'function') {
      const validate = formControlSchema.validate();
      validate.push(new IsRequiredValidation());
      formControlSchema.validate = () => validate;
    } else {
      formControlSchema.validate.push(new IsRequiredValidation());
    }
  },
  [FormControlDependsOnThenEnum.SetValue]: (_key: string, _records: Record<string, FieldSchema>) => {
    throw new Error('Not implemented');
  },
  [FormControlDependsOnThenEnum.AddValidations]: (_key: string, _records: Record<string, FieldSchema>) => {
    throw new Error('Not implemented');
  },
};
